import React, { forwardRef, useEffect, useImperativeHandle } from "react";
import { Button, Col, Modal, ProgressBar, Row, Table } from "react-bootstrap";
import { FleetStatPopover } from "pages/dashboard/main/widgets/fleet_stats/FleetStatPopover";
import { useSuperState } from "helpers/hooks";
import { FormattedMessage } from "react-intl";

export const FleetStatsModal = forwardRef((props, ref) => {

    const [state, setState, resetState] = useSuperState({
        show: false,
        stats: null
    });

    const handleShow = async stats => {

        setState({
            show: true,
            stats
        });

    };

    const handleHide = () => resetState();

    useImperativeHandle(ref, () => ({
        show: handleShow
    }));

    useEffect(() => resetState, []);

    const { stats, show } = state;

    const pressure_pct = stats
        ? stats?.available_user_sessions == 0
            ? 100
            : Math.floor(stats?.active_user_sessions / (stats?.active_user_sessions + stats?.available_user_sessions) * 100)
        : 0;

    const pressure_variant = pressure_pct > 66
        ? 'danger'
        : (pressure_pct > 33
            ? 'warning'
            : 'success');

    return (
        <>
            <Modal show={show} onHide={handleHide} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>
                        Fleet Details
                    </Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    <h6>
                        <FormattedMessage
                            id="label.system_pressure_pressure_pctpercen.5fe920cb"
                            defaultMessage="System Pressure ({pressure_pct}%)"
                            values={{ pressure_pct }}
                       />
                    </h6>
                    <ProgressBar now={pressure_pct} variant={pressure_variant}/>
                    <h6 className="mt-3">
                        <FormattedMessage
                            id="label.stats"
                            defaultMessage="Stats"
                        />
                    </h6>
                    <Row>
                        <Col sm={5}>
                            <small>
                                <Table bordered className="mb-0" striped>
                                    <tbody>
                                        <tr>
                                            <th>
                                                <FormattedMessage
                                                    id="label.active_user_sessions"
                                                    defaultMessage="Active User Sessions"
                                                />
                                                &nbsp;
                                                <FleetStatPopover
                                                    title="Active User Sessions"
                                                    body="The number of user sessions currently being used for streaming sessions"
                                                />
                                            </th>
                                            <td>
                                                {stats?.active_user_sessions}
                                            </td>
                                        </tr>
                                        <tr>
                                            <th>
                                                <FormattedMessage
                                                    id="label.available_user_sessions"
                                                    defaultMessage="Available User Sessions"
                                                />
                                                &nbsp;
                                                <FleetStatPopover
                                                    title="Available User Sessions"
                                                    body="The number of idle session slots currently available for user sessions."
                                                />
                                            </th>
                                            <td>
                                                {stats?.available_user_sessions}
                                            </td>
                                        </tr>
                                        <tr>
                                            <th>
                                                <FormattedMessage
                                                    id="label.actual_user_sessions"
                                                    defaultMessage="Actual User Sessions"
                                                />
                                                &nbsp;
                                                <FleetStatPopover
                                                    title="Actual User Sessions"
                                                    body="The total number of session slots that are available for streaming or are currently streaming."
                                                />
                                            </th>
                                            <td>
                                                {stats?.actual_user_sessions}
                                            </td>
                                        </tr>
                                        <tr>
                                            <th>
                                                <FormattedMessage
                                                    id="label.desired_user_sessions"
                                                    defaultMessage="Desired User Sessions"
                                                />
                                                &nbsp;
                                                <FleetStatPopover
                                                    title="Desired User Sessions"
                                                    body="The total number of sessions slots that are either running or pending. This represents the total number of concurrent streaming sessions your fleet can support in a steady state."
                                                />
                                            </th>
                                            <td>
                                                {stats?.desired_user_sessions}
                                            </td>
                                        </tr>
                                        <tr>
                                            <th>
                                                <FormattedMessage
                                                    id="label.max_sessions_per_instance"
                                                    defaultMessage="Max Sessions per Instance"
                                                />
                                                &nbsp;
                                                <FleetStatPopover
                                                    title="Max Sessions per Instance"
                                                    body="The maximum number of user sessions on an instance. This only applies to multi-session fleets."
                                                />
                                            </th>
                                            <td>
                                                {stats?.max_sessions_per_instance}
                                            </td>
                                        </tr>
                                    </tbody>
                                </Table>
                            </small>
                        </Col>
                        <Col sm={7}>
                            <small>
                                <Table bordered className="mb-0" striped>
                                    <tbody>
                                        <tr>
                                            <th>
                                                <FormattedMessage
                                                    id="label.image"
                                                    defaultMessage="Image"
                                                />
                                            </th>
                                            <td>
                                                {stats?.image_name}
                                            </td>
                                        </tr>

                                        <tr>
                                            <th>
                                                In Use Instances
                                                &nbsp;
                                                <FleetStatPopover
                                                    title="In Use Instances"
                                                    body="The number of instances in use for streaming."
                                                />
                                            </th>
                                            <td>
                                                {stats?.in_use}
                                            </td>
                                        </tr>
                                        <tr>
                                            <th>
                                                Running Instances
                                                &nbsp;
                                                <FleetStatPopover
                                                    title="Running Instances"
                                                    body="The total number of simultaneous streaming instances that are running."
                                                />
                                            </th>
                                            <td>
                                                {stats?.running}
                                            </td>
                                        </tr>
                                        <tr>
                                            <th>
                                                Available Instances
                                                &nbsp;
                                                <FleetStatPopover
                                                    title="Available Instances"
                                                    body="The number of currently available instances that can be used to stream sessions."
                                                />
                                            </th>
                                            <td>
                                                {stats?.available}
                                            </td>
                                        </tr>

                                        <tr>
                                            <th>
                                                Desired Instances
                                                &nbsp;
                                                <FleetStatPopover
                                                    title="Desired Instances"
                                                    body="The desired number of streaming instances."
                                                />
                                            </th>
                                            <td>
                                                {stats?.max_sessions_per_instance}
                                            </td>
                                        </tr>
                                    </tbody>
                                </Table>
                            </small>
                        </Col>
                    </Row>
                </Modal.Body>

                <Modal.Footer>
                    <Button variant="secondary" onClick={handleHide}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );

});